import {
  GOOGLE_ADS_ID,
  GOOGLE_ADS_LINKER,
  isGoogleAdsMeasurementHost,
  isProductionWebAnalyticsRuntime,
} from '@genie/shared/client';
import { useEffect } from 'react';

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

/** GA4 + Google Ads — production hosts only (not local Docker dev). */
export function ProductionGoogleAnalytics(): null {
  useEffect(() => {
    if (!isProductionWebAnalyticsRuntime(import.meta.env.PROD)) return;

    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();
    const trackAds = isGoogleAdsMeasurementHost(window.location.hostname);
    if (!measurementId && !trackAds) return;

    let cancelled = false;

    void (async () => {
      window.dataLayer = window.dataLayer ?? [];
      window.gtag =
        window.gtag ??
        function gtag(...args: unknown[]) {
          window.dataLayer!.push(args);
        };

      const scriptId = measurementId || GOOGLE_ADS_ID;
      await loadScript(
        `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(scriptId)}`,
      );
      if (cancelled) return;

      window.gtag('js', new Date());
      if (measurementId) {
        window.gtag('config', measurementId, {
          anonymize_ip: true,
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
        });
      }
      if (trackAds) {
        window.gtag('set', 'linker', { ...GOOGLE_ADS_LINKER });
        window.gtag('config', GOOGLE_ADS_ID, {
          linker: { ...GOOGLE_ADS_LINKER },
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
